import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { v4 as uuidv4 } from 'uuid';
import { create } from 'zustand';

import { useCustomAppClient } from 'api/custom-client';
import {
    buildAutoFormRequest,
    unwrapAutoFormResponse
} from 'app/modules/wallforming/components/auto-form/utils/autoFormUtils';
import { isPlanarFacePoi } from 'app/modules/wallforming/components/scenes/objects/pois/utils/poiUtils';
import { usePlacementStore } from 'app/modules/wallforming/components/state-management/placement';
import { applyPlacementGroups } from 'app/modules/wallforming/components/state-management/placement/utils/applyPlacementGroups';
import { EMPTY_GUID } from 'shared/constants';
import { toastNotifications } from 'shared/utils/toastNotifications';

import type { AutoFormBuildingResponse } from 'app/modules/wallforming/components/auto-form/types/autoFormTypes';

interface AutoFormState {
    isRunning: boolean;
    lastResult: AutoFormBuildingResponse | null;
    /** Stops the running request locally and on the server; `null` when nothing runs. */
    cancel: (() => void) | null;
}

/** Shared so the toolbar button reflects runs started elsewhere (e.g. right after PDF → IFC). */
export const useAutoFormStore = create<AutoFormState>(() => ({ isRunning: false, lastResult: null, cancel: null }));

export const useAutoFormBuilding = ({ anchorsOnly = false }: { anchorsOnly?: boolean } = {}) => {
    const { t } = useTranslation();
    const [searchParams] = useSearchParams();
    const sessionId = useMemo(() => searchParams.get('formingsession') ?? EMPTY_GUID, [searchParams]);
    const postAutoForm = useCustomAppClient<unknown>();
    const cancelOperation = useCustomAppClient<void>();

    return useCallback(async (): Promise<AutoFormBuildingResponse | null> => {
        if (useAutoFormStore.getState().isRunning) {
            return null;
        }

        const abortController = new AbortController();
        const operationId = uuidv4();
        useAutoFormStore.setState({
            isRunning: true,
            cancel: () => {
                abortController.abort();
                // Explicit server-side cancellation: proxies may not forward the aborted connection.
                void cancelOperation({ url: `/operations/${operationId}`, method: 'DELETE' }).catch(() => undefined);
            }
        });
        try {
            const state = usePlacementStore.getState();
            const revision = state.workspaceRevision;
            const request = buildAutoFormRequest(state, sessionId, import.meta.env.VITE_DEFAULT_SYSTEM_ID);
            if (anchorsOnly) {
                const selectedIndex = state.poiSelection.current.index;
                const poi = selectedIndex === undefined ? undefined : state.pois[selectedIndex]?.poi;
                if (!poi || !isPlanarFacePoi(poi) || poi.poiGuid === EMPTY_GUID) {
                    throw new Error(t('infoAutoAnchorPlacement'));
                }
                request.stages = ['Anchors'];
                request.poiIds = [poi.poiGuid];
            }
            const result = unwrapAutoFormResponse(
                await postAutoForm({
                    url: '/forming/auto-form',
                    method: 'POST',
                    headers: { 'X-Operation-Id': operationId },
                    data: request,
                    signal: abortController.signal
                })
            );
            if (abortController.signal.aborted) {
                throw new DOMException('Auto-forming cancelled.', 'AbortError');
            }
            useAutoFormStore.setState({ lastResult: result });

            if (!result.accepted) {
                toastNotifications.error({ title: t('error'), message: result.message || t('errorAutoForm') });
                return result;
            }

            const current = usePlacementStore.getState();
            if (current.workspaceRevision !== revision) {
                throw new Error(t('copilotStaleContextError'));
            }

            const { summary } = result;
            if (result.placements.length === 0) {
                toastNotifications.information({ title: t('info'), message: result.message });
                return result;
            }
            const existingIds = new Set(
                Object.values(current.articles).flatMap((article) => Object.keys(article.articleInstances))
            );
            applyPlacementGroups({
                placements: result.placements.map((group) => ({
                    ...group,
                    instances: group.instances.filter((instance) => !existingIds.has(instance.id))
                }))
            });

            toastNotifications.success({
                title: t('success'),
                message: anchorsOnly
                    ? t('successAutoAnchorPlacement', { count: summary.anchorInstances })
                    : t('successAutoForm', {
                          corners: summary.cornerInstances,
                          panels: summary.panelInstances,
                          connectors: summary.connectorInstances,
                          anchors: summary.anchorInstances,
                          seconds: (summary.elapsedMilliseconds / 1000).toFixed(1)
                      })
            });
            if (summary.failedSteps > 0) {
                toastNotifications.information({
                    title: t('info'),
                    message: t('infoAutoFormFailedSteps', { count: summary.failedSteps })
                });
            }

            return result;
        } catch (error) {
            if (abortController.signal.aborted) {
                toastNotifications.information({ title: t('info'), message: t('infoAutoFormCancelled') });
                return null;
            }
            toastNotifications.error({
                title: t('error'),
                message: error instanceof Error ? error.message : t('errorAutoForm')
            });
            return null;
        } finally {
            useAutoFormStore.setState({ isRunning: false, cancel: null });
        }
    }, [anchorsOnly, cancelOperation, postAutoForm, sessionId, t]);
};
